import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import CanonicalTag from "@/components/CanonicalTag";
import { VideoModal } from "@/components/VideoModal";
import { ImageGalleryModal } from "@/components/ImageGalleryModal";
import { Play } from "lucide-react";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer } from "@/components/neumorphic";

interface Photo {
  id: string;
  title: string;
  category: string;
  imageUrl: string;
  description: string;
}
interface Video {
  id: string;
  title: string;
  category: string;
  videoUrl: string;
  thumbnailUrl: string;
  description: string;
}
const categories = [{
  id: "adu",
  name: "ADU"
}, {
  id: "house",
  name: "House"
}, {
  id: "interior",
  name: "Interior"
}, {
  id: "kitchen",
  name: "Kitchen"
}, {
  id: "bathroom",
  name: "Bathroom"
}];

// ADU subcategories
const aduSubcategories = [{
  id: "gilmour",
  name: "Gilmour",
  fullDescription: "Gilmour - Studio, 1 Bath - 378 SF"
}, {
  id: "di-meola",
  name: "Di Meola",
  fullDescription: "Di Meola - 1 Bed, 1 Bath - 389 SF"
}, {
  id: "satriani",
  name: "Satriani",
  fullDescription: "Satriani - 1 Bed, 1 Bath - 541 SF"
}, {
  id: "knopfler",
  name: "Knopfler",
  fullDescription: "Knopfler - 1 Bed, 1 Bath - 797 SF"
}, {
  id: "moore",
  name: "Moore",
  fullDescription: "Moore - 1 Bed, 1 Bath - 815 SF"
}, {
  id: "los-altos-1",
  name: "LA Wee",
  fullDescription: "LA Wee - 1 Bed, 1 Bath - 525 SF"
}, {
  id: "los-altos-2",
  name: "LA Median",
  fullDescription: "LA Median - 1 Bed, 1 Bath - 854 SF"
}, {
  id: "los-altos-3",
  name: "LA Grand",
  fullDescription: "LA Grand - 2 Bed, 2 Bath - 1190 SF"
}, {
  id: "other-projects",
  name: "Other Projects",
  fullDescription: "Other Projects"
}];

// Photos for each category
const photos: Photo[] = [
// ADU photos
{
  id: "adu1",
  title: "Contemporary Cube ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/ee8bd633-658a-4cbf-bb86-a2b8fb69c5fd.png",
  description: "Contemporary cube-shaped ADU with flat roof, wood accent trim, and glass walls in forest landscape."
}, {
  id: "adu2",
  title: "Modern Black ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/75108e2e-3bad-4e91-8f94-52420907c290.png",
  description: "Modern ADU with black metal siding, large sliding glass doors, and landscaped courtyard with succulents."
}, {
  id: "adu3",
  title: "Blue ADU with Open Doors",
  category: "adu",
  imageUrl: "/lovable-uploads/508785b0-ff40-4f19-8105-53b1e178e877.png",
  description: "Backyard ADU with blue exterior and fully open accordion doors revealing kitchen and dining space."
}, {
  id: "adu4",
  title: "Coastal Blue ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/5b127c9c-0084-4d62-a075-45685550c3e7.png",
  description: "Charming blue ADU with patio seating and sliding glass doors, styled with beachy Adirondack chairs."
}, {
  id: "adu5",
  title: "Modern Minimalist ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/bc636e24-1e40-4c46-9ace-fc6efc3e545c.png",
  description: "Luxury modern ADU with sleek black and white design, indoor-outdoor layout, and open patio seating."
}, {
  id: "adu6",
  title: "Farmhouse Style ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/adea23ce-4a81-411d-a25e-38a555745f2a.png",
  description: "Farmhouse-style ADU with white siding, black windows, and expansive indoor-outdoor living."
}, {
  id: "adu7",
  title: "White Contemporary ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/48e5ae47-5608-472e-a292-2c7ed01c7996.png",
  description: "Minimalist white ADU with flat roof, black trim, and symmetrical front elevation with three windows."
}, {
  id: "adu8",
  title: "Modern Farmhouse ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/3d721daa-9104-4b7a-9a4f-e9d5b3bdf634.png",
  description: "Modern farmhouse ADU with gable roof, mix of white and wood siding, and private patio deck."
}, {
  id: "adu9",
  title: "Two-Story Modern ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/68f369b3-08e6-4c0d-a567-8ab08c9c089a.png",
  description: "Contemporary farmhouse ADU featuring board and batten facade and open porch with wood accents."
}, {
  id: "adu10",
  title: "Vertical Wood Panel ADU",
  category: "adu",
  imageUrl: "/lovable-uploads/3894b7f9-5236-40cd-a1c2-a70d9af6f58a.png",
  description: "Two-story ADU with mixed cladding, flat roof, and wood-panel detailing on ground floor elevation."
},
// House photos
{
  id: "house1",
  title: "Modern Box Home",
  category: "house",
  imageUrl: "/lovable-uploads/9ceebd4f-3a0b-4cce-825c-90fb7b8becff.png",
  description: "Modern two-story home with bold black and white contrast, glass balcony, and drought-tolerant landscaping."
}, {
  id: "house2",
  title: "Minimal Box Home",
  category: "house",
  imageUrl: "/lovable-uploads/f19526cf-2636-47be-8400-f9bbbdcefd00.png",
  description: "Minimalist box-style home with clean white facade, black trim, and courtyard layout."
}, {
  id: "house3",
  title: "Modern Luxury Home",
  category: "house",
  imageUrl: "/lovable-uploads/1128840f-3cce-4421-8a2b-10766f56549b.png",
  description: "Luxury modern home with expansive floor-to-ceiling windows and lush front yard."
}, {
  id: "house4",
  title: "Modern Farmhouse Home",
  category: "house",
  imageUrl: "/lovable-uploads/4e5ac9d9-c521-424c-b3d9-20ba55bd06c8.png",
  description: "Single-story modern farmhouse with garage, wood accents, and manicured lawn."
}, {
  id: "house5",
  title: "Mid-Century Home",
  category: "house",
  imageUrl: "/lovable-uploads/d0918aa8-0178-4c42-b111-33b84dcc3704.png",
  description: "Mid-century style white home with geometric forms and clerestory windows."
}, {
  id: "house6",
  title: "Modern Slope Lot Home",
  category: "house",
  imageUrl: "/lovable-uploads/24ceb933-ab87-4372-8b67-694319a869af.png",
  description: "Modern hillside home with layered landscape steps and vertical wood accents."
}, {
  id: "house7",
  title: "Contemporary Home",
  category: "house",
  imageUrl: "/lovable-uploads/458f450e-1980-4b64-b17a-6bd5a01ca532.png",
  description: "Contemporary home with centered firepit courtyard and black steel roofing."
}, {
  id: "house8",
  title: "Modern House",
  category: "house",
  imageUrl: "/lovable-uploads/c1347ea8-0aed-449b-b4a4-3f1d9e9cb798.png",
  description: "Modern home with smooth stucco walls, natural wood front door, and potted plants."
}, {
  id: "house9",
  title: "Mid-Century Ranch Home",
  category: "house",
  imageUrl: "/lovable-uploads/a33f5ecb-052e-437a-85f3-5b814da62cd5.png",
  description: "Ranch-style mid-century home with black exterior, white garage door, and wood entry."
},
// Interior photos
{
  id: "interior1",
  title: "Vaulted Ceiling Living Room",
  category: "interior",
  imageUrl: "/lovable-uploads/55e33f9c-6e09-4ed6-b66c-2764cff17804.png",
  description: "Spacious living room with vaulted ceiling, skylights, modern sectional sofa, and rich wood beams."
}, {
  id: "interior2",
  title: "Colorful Dining Room",
  category: "interior",
  imageUrl: "/lovable-uploads/d72ce1cf-35ad-4cf0-9f70-3b861a884339.png",
  description: "Eclectic dining area with rustic wood table, colorful modern art, and pendant light fixture."
}, {
  id: "interior3",
  title: "Grand Foyer Entry",
  category: "interior",
  imageUrl: "/lovable-uploads/5f55c926-7bd9-4591-aea7-60bd3938a31e.png",
  description: "Elegant foyer with double glass doors, grand staircase, and decorative tile inlay."
}, {
  id: "interior4",
  title: "Coastal Themed Bookshelf",
  category: "interior",
  imageUrl: "/lovable-uploads/c3ad12eb-c516-4ec9-94dc-8b4a6d9bcd9a.png",
  description: "Coastal-themed built-in bookshelf surrounded by aqua-painted walls and nautical decor."
}, {
  id: "interior5",
  title: "Beachy Living Room",
  category: "interior",
  imageUrl: "/lovable-uploads/3cd02972-2d2d-4cae-9ba5-b547691c10d9.png",
  description: "Living room with multi-color plank wood fireplace, coastal decor, and relaxed furniture."
}, {
  id: "interior6",
  title: "Fireplace Wood Accent Wall",
  category: "interior",
  imageUrl: "/lovable-uploads/28fda9ed-0ada-4c46-850f-6bbe96ee8471.png",
  description: "Interior with TV-mounted fireplace on reclaimed wood accent wall and light teal color scheme."
}, {
  id: "interior7",
  title: "Coastal Living Room",
  category: "interior",
  imageUrl: "/lovable-uploads/8bdda64a-64d0-416d-9bd7-730481d957cb.png",
  description: "Surf-inspired living room with wave art, surfboard decor, and mid-century glass table."
}, {
  id: "interior8",
  title: "Beach Style Dining Room",
  category: "interior",
  imageUrl: "/lovable-uploads/f84ac62f-df2e-4a03-b2cf-44205a6dac0c.png",
  description: "Open dining space with teal blue wall, woven chairs, and coastal lighting feature."
}, {
  id: "interior9",
  title: "ADU Kitchen Island",
  category: "interior",
  imageUrl: "/lovable-uploads/3ee7f03f-a3bc-4253-87c4-f2ad9a7a4664.png",
  description: "Kitchen island view with stainless sink overlooking living space and open accordion glass doors."
}, {
  id: "interior10",
  title: "Covered Outdoor Lounge",
  category: "interior",
  imageUrl: "/lovable-uploads/07dfc299-225b-4d3c-b4c3-7ae077c4b1ee.png",
  description: "Covered outdoor lounge area with wood-clad fireplace, TV, and modern seating."
}, {
  id: "interior11",
  title: "Aqua Dining Room",
  category: "interior",
  imageUrl: "/lovable-uploads/c5e92a33-d12b-4ddb-b4ee-2631c7ed0a34.png",
  description: "Colorful aqua-toned dining space with glass-front cabinets, floating shelves, and beach decor."
}, {
  id: "interior12",
  title: "Modern Entry",
  category: "interior",
  imageUrl: "/lovable-uploads/0402f4be-93a9-4e6f-83c9-f390ea2aa5b6.png",
  description: "Entryway with black double doors, tan leather bench, and white panel molding."
}, {
  id: "interior13",
  title: "Aqua Bar Wall",
  category: "interior",
  imageUrl: "/lovable-uploads/233eda1d-c1bb-4e96-8896-8630da8837ca.png",
  description: "Coastal bar wall with floating shelves, nautical collectibles, and bright aqua walls."
}, {
  id: "interior14",
  title: "Open Layout Kitchen Dining",
  category: "interior",
  imageUrl: "/lovable-uploads/667ed660-4768-4028-8398-2f35700c5221.png",
  description: "Open kitchen and dining room with large wood table, pendant lights, and blue island."
}, {
  id: "interior15",
  title: "Living Room Gray Sofa",
  category: "interior",
  imageUrl: "/lovable-uploads/c0755f22-fb00-4b51-a352-cb295a8dd890.png",
  description: "Living space with vaulted ceiling, gray sectional, yellow dining chairs, and accordion glass doors."
}, {
  id: "interior16",
  title: "Kitchen Living Room Open Concept",
  category: "interior",
  imageUrl: "/lovable-uploads/5ead1609-cc1f-4591-8777-503d1d22c1b7.png",
  description: "Kitchen and living room combination with floral stools, gray couch, and white herringbone backsplash."
}, {
  id: "interior17",
  title: "Orange Wall Home Bar",
  category: "interior",
  imageUrl: "/lovable-uploads/369ce9bc-9ad6-4240-a127-845a56fc94d7.png",
  description: "Contemporary home bar with bold orange accent wall, floating racks, and glass shelving."
}, {
  id: "interior18",
  title: "Modern Home Bar",
  category: "interior",
  imageUrl: "/lovable-uploads/2b0b3af3-d756-48c3-9a1c-1bb8d5ef5a0d.png",
  description: "Elegant indoor bar with dual counter levels, dark cabinets, and pendant lighting."
}, {
  id: "interior19",
  title: "Farmhouse Dining Room",
  category: "interior",
  imageUrl: "/lovable-uploads/fa6552a7-324d-4794-863e-9d9fe4a9355d.png",
  description: "Rustic dining room with bronze pendant lighting, wall art, and large wood table."
}, {
  id: "interior20",
  title: "Hallway Laundry Pullout",
  category: "interior",
  imageUrl: "/lovable-uploads/22ea6f20-1de0-474e-9eda-8d8316f2230f.png",
  description: "Bright hallway with turquoise walls and a built-in white pullout laundry cabinet."
}, {
  id: "interior21",
  title: "Navy Fireplace Living Room",
  category: "interior",
  imageUrl: "/lovable-uploads/591d653c-2d77-4a80-8260-2e9da4322b35.png",
  description: "Living room with navy vertical shiplap fireplace, rustic chandelier, and gray sectional."
}, {
  id: "interior22",
  title: "Coastal Hallway Teal Walls",
  category: "interior",
  imageUrl: "/lovable-uploads/439499d5-c85d-48b1-8711-da3562568285.png",
  description: "Bright hallway with teal walls, skylight, and beach-inspired decor."
}, {
  id: "interior23",
  title: "Living Room Circular Chandelier",
  category: "interior",
  imageUrl: "/lovable-uploads/1d31afe5-ca0c-433c-8f41-cc41ae45b9f7.png",
  description: "Living room with large round chandelier, navy fireplace, and white sectional sofas."
}, {
  id: "interior24",
  title: "White Living Room Fireplace View",
  category: "interior",
  imageUrl: "/lovable-uploads/045c9713-fc7d-47ab-8f98-5ee5bdd0aeb5.png",
  description: "Spacious open-concept living room with fireplace and connected dining/kitchen."
}, {
  id: "interior25",
  title: "Accordion Door Indoor Outdoor",
  category: "interior",
  imageUrl: "/lovable-uploads/07742ba5-337c-4155-8432-cece1acdbe88.png",
  description: "Open wall view showing patio fireplace from inside living room."
}, {
  id: "interior26",
  title: "Modern White Hallway Gallery",
  category: "interior",
  imageUrl: "/lovable-uploads/9cb6fc36-840b-4f5b-a74b-8d9ad19dc0b7.png",
  description: "White hallway with wood floors, gallery wall photos, and tall built-in storage."
}, {
  id: "interior27",
  title: "Moody Bedroom Dark Headboard",
  category: "interior",
  imageUrl: "/lovable-uploads/4625872f-ed58-4608-ab6a-e594b5196078.png",
  description: "Moody master bedroom with deep green paneled wall and antique black headboard."
}, {
  id: "interior28",
  title: "Master Bedroom Green Panel Wall",
  category: "interior",
  imageUrl: "/lovable-uploads/c51f23eb-430e-42b6-afbf-c4e54d033948.png",
  description: "Bright bedroom with grid-style paneled green wall and accent orange pillow."
}, {
  id: "interior29",
  title: "Mid-Century Modern Living Room",
  category: "interior",
  imageUrl: "/lovable-uploads/66f5a490-0547-485c-802d-f77fbf50d19a.png",
  description: "Mid-century modern living room with framed abstract art, wood furniture, and soft seating."
}, {
  id: "interior30",
  title: "Fireplace Living Room White Accents",
  category: "interior",
  imageUrl: "/lovable-uploads/b7e34c6d-bdad-4cbf-bdfb-d6b0cdf14ff9.png",
  description: "Living space featuring white-trimmed fireplace, round chandelier, and corner windows."
},
// Kitchen photos
{
  id: "kitchen1",
  title: "Modern Dual-Tone Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/1ac2dacd-897e-4a2d-8f1c-6a2f2074602f.png",
  description: "Modern dual-tone kitchen featuring a spacious island, green and white cabinetry, and an adjacent dining area with outdoor access."
}, {
  id: "kitchen2",
  title: "Green Cabinet Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/7aa79ad7-3d8d-406c-8d79-b1aac2572447.png",
  description: "Elegant kitchen with dark green cabinets, white countertops, and brass fixtures, illuminated by globe pendant lights."
}, {
  id: "kitchen3",
  title: "Navy and Wood Farmhouse Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/96df8c88-bfef-4aa6-8150-81d1f458d3c9.png",
  description: "Rustic farmhouse kitchen with navy cabinetry, wood countertops, and exposed ceiling beams over a brick backsplash."
}, {
  id: "kitchen4",
  title: "Sleek Navy Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/8f341a07-c56c-44b0-8f10-2f15b0ecd175.png",
  description: "Contemporary navy kitchen with white quartz counters, wood ceiling, and bar seating in a minimalist layout."
}, {
  id: "kitchen5",
  title: "Warm Modern Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/c0fdcfae-90f6-4856-84ae-505e8c73438b.png",
  description: "Modern kitchen design with a gold ceiling panel, pendant lighting, and green velvet stools facing a large island."
}, {
  id: "kitchen6",
  title: "Luxury Black Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/9513abff-898b-4af6-aa46-ff153c3051b0.png",
  description: "Luxurious black kitchen featuring marble countertops, glass cabinets, and a dramatic glass pendant light."
}, {
  id: "kitchen7",
  title: "Contemporary Black Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/95ad554c-f875-4e9b-b392-67809d399de8.png",
  description: "Contemporary kitchen with rich black cabinetry, walnut wood countertops, and an open layout connecting to the dining space."
}, {
  id: "kitchen8",
  title: "Industrial Style Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/efb6af6c-d1f3-4135-9097-e5910cb9c84c.png",
  description: "Industrial modern kitchen with open wood shelving, black cabinets, and concrete-style countertops with pendant lighting."
}, {
  id: "kitchen9",
  title: "Elegant Kitchen with Green Cabinets",
  category: "kitchen",
  imageUrl: "/lovable-uploads/27067a38-c6f0-4642-a53f-9061528b9a54.png",
  description: "Modern kitchen with flat-panel green cabinets, built-in shelving, and a waterfall edge island accented by warm lighting."
}, {
  id: "kitchen10",
  title: "Navy Blue Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/3e4148a3-e788-419a-b91b-44c199faf373.png",
  description: "Classic navy kitchen with gold trim, white hood range, and a chevron wood floor for a high-end traditional aesthetic."
}, {
  id: "kitchen11",
  title: "Navy and White Wine Storage Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/44be631e-54cb-4333-bcfb-0acc3d4b668a.png",
  description: "Elegant navy and white kitchen with wine storage and herringbone tile backsplash"
}, {
  id: "kitchen12",
  title: "Navy Island with Wine Refrigerator",
  category: "kitchen",
  imageUrl: "/lovable-uploads/1d65a9f6-a975-41a7-bb95-6eed05b59117.png",
  description: "Built-in wine fridge in navy blue island cabinetry with modern shaker style"
}, {
  id: "kitchen13",
  title: "Luxury Navy and Gold Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/c549b463-aaa6-4979-a0b9-ed86806df43d.png",
  description: "Luxury white and navy kitchen with black tile backsplash and gold fixtures"
}, {
  id: "kitchen14",
  title: "Modern Gray Dual Island Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/39a0e0b6-83eb-4d0b-ba2e-03fd85ee9931.png",
  description: "Spacious gray and white kitchen with dual islands and modern pendant lighting"
}, {
  id: "kitchen15",
  title: "Bright Kitchen with Black Island",
  category: "kitchen",
  imageUrl: "/lovable-uploads/624b4f96-013c-4111-a279-51de2a7e579c.png",
  description: "Bright white kitchen with black island and farmhouse sink under skylight"
}, {
  id: "kitchen16",
  title: "Traditional Wine Storage Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/108a9089-b433-4791-afd3-908a8d43a02a.png",
  description: "Luxury white and navy kitchen with black tile backsplash and gold fixtures"
}, {
  id: "kitchen17",
  title: "Modern Black Butcher Block Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/20dbae3d-bc3e-4036-9117-8ce77d10eca0.png",
  description: "Modern black kitchen with butcher block countertops and open wooden shelves"
}, {
  id: "kitchen18",
  title: "Open Concept Blue Island Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/ec051f78-7c82-4180-8361-6790a355a98a.png",
  description: "Open concept kitchen with blue island, gold hardware, and floral bar stools"
}, {
  id: "kitchen19",
  title: "White Kitchen with Dark Island",
  category: "kitchen",
  imageUrl: "/lovable-uploads/f01ef973-e9a6-46af-8c5c-8df218604f5f.png",
  description: "White and gray kitchen with chrome appliances and geometric backsplash"
}, {
  id: "kitchen20",
  title: "Blue Island Open Layout Kitchen",
  category: "kitchen",
  imageUrl: "/lovable-uploads/1d8bcc24-8c7c-4c21-b65e-faa021c002ab.png",
  description: "Contemporary white kitchen with sleek gray backsplash and gas range stove"
}, {
  id: "kitchen21",
  title: "Kitchen White Warm Wood Farmhouse Style",
  category: "kitchen",
  imageUrl: "/lovable-uploads/9c54e9f9-ffe9-4691-98ec-f828fa5f1ba3.png",
  description: "Modern white kitchen with warm wood tones and farmhouse dining table."
}, {
  id: "kitchen22",
  title: "Kitchen Herringbone Tile Range Hood Shelving",
  category: "kitchen",
  imageUrl: "/lovable-uploads/3814ddee-7e83-4660-b81e-e484bd25d26f.png",
  description: "Close-up of range hood with herringbone tile backsplash and floating shelves."
}, {
  id: "kitchen23",
  title: "Kitchen Transitional Shelving Under Window",
  category: "kitchen",
  imageUrl: "/lovable-uploads/6ed68ae9-2fc1-43e3-b5bb-14e9562926bd.png",
  description: "Transitional kitchen layout with open shelving below a large window."
}, {
  id: "kitchen24",
  title: "Kitchen Window Centered Floating Shelves",
  category: "kitchen",
  imageUrl: "/lovable-uploads/126c651d-2c64-4e99-b94f-c4e27aa359ba.png",
  description: "Clean kitchen sink view with centered window and minimalist shelving."
}, {
  id: "kitchen25",
  title: "Kitchen Marble Counter Open Layout View",
  category: "kitchen",
  imageUrl: "/lovable-uploads/8d24647f-0045-4fab-b649-f37c6a39debc.png",
  description: "Wide kitchen view showing marble countertops and open layout."
}, {
  id: "kitchen26",
  title: "Kitchen Wine Bar Coffee Station Gray Cabinets",
  category: "kitchen",
  imageUrl: "/lovable-uploads/ebb351cc-6a5a-45be-be1b-3910d37cddad.png",
  description: "Gray cabinet kitchen wet bar with wine cooler and small appliances."
}, {
  id: "kitchen27",
  title: "Kitchen Coffee Station Wine Storage Modern",
  category: "kitchen",
  imageUrl: "/lovable-uploads/e2b4ec3c-8256-427e-b8e8-dae2c3b275c5.png",
  description: "Modern kitchen bar with built-in wine rack and overhead glass storage."
}, {
  id: "kitchen28",
  title: "Kitchen Coffee Station Wine Glass Holder",
  category: "kitchen",
  imageUrl: "/lovable-uploads/d1af2098-8c87-43ef-925a-3187897c8a9e.png",
  description: "Detail of kitchen bar glassware storage and mini wine fridge."
}, {
  id: "kitchen29",
  title: "Kitchen Modern Nautical Decor Wet Bar",
  category: "kitchen",
  imageUrl: "/lovable-uploads/20faba38-94bc-45f3-8712-6b8da4a97d8c.png",
  description: "Nautical-themed wet bar with decorative items and glass-front cabinets."
}, {
  id: "kitchen30",
  title: "Kitchen Wet Bar Glass Display Lighting",
  category: "kitchen",
  imageUrl: "/lovable-uploads/1e694260-917b-4dc3-9387-2cc25b5e95b8.png",
  description: "Compact kitchen bar with lighting and upper glass display cabinets."
}, {
  id: "kitchen31",
  title: "Modern Farmhouse Kitchen with Wood Island and Black Chairs",
  category: "kitchen",
  imageUrl: "/lovable-uploads/3582fe2e-bc47-4c0f-b0f5-da1d806e1b03.png",
  description: "Modern farmhouse kitchen featuring a large wood dining table, black chairs, and white cabinetry with a bold blue island."
}, {
  id: "kitchen32",
  title: "Kitchen with White Cabinets Blue Island and Pendant Lights",
  category: "kitchen",
  imageUrl: "/lovable-uploads/d1c3e781-1a22-4db7-b618-8daaf9d4f3a2.png",
  description: "Kitchen with white shaker cabinets, a bright blue island, globe pendant lights, and stainless steel appliances."
}, {
  id: "kitchen33",
  title: "Turquoise Galley Kitchen with Black Appliances and Skylight",
  category: "kitchen",
  imageUrl: "/lovable-uploads/28fe63b5-2ea3-4cf3-991a-b4f937dbb356.png",
  description: "Narrow galley kitchen with turquoise cabinets, black appliances, and a skylight bringing in natural light."
}, {
  id: "kitchen34",
  title: "White Kitchen with Black Window and Farmhouse Sink",
  category: "kitchen",
  imageUrl: "/lovable-uploads/cc379c2c-ce13-4b9a-b97c-331edcb656ae.png",
  description: "White kitchen with black framed window, farmhouse sink, and minimalistic cabinet design."
}, {
  id: "kitchen35",
  title: "White Modern Kitchen with Large Clock and Commercial Range",
  category: "kitchen",
  imageUrl: "/lovable-uploads/45406089-dca3-4c3b-94f8-6e29bc240b98.png",
  description: "Contemporary white kitchen with a large clock, commercial gas range, and white subway tile backsplash."
}, {
  id: "kitchen36",
  title: "Turquoise Galley Kitchen with Open Dining View",
  category: "kitchen",
  imageUrl: "/lovable-uploads/0114c63b-5361-41d4-8633-feac775819c0.png",
  description: "Turquoise galley-style kitchen with colorful backsplash, open shelving, and view into dining area."
}, {
  id: "kitchen37",
  title: "Closeup of Agate Backsplash with Modern Kitchen Sink",
  category: "kitchen",
  imageUrl: "/lovable-uploads/6b042ec4-fde5-4781-b9ad-7d8fba8d8d8c.png",
  description: "Close-up of luxury agate tile backsplash behind a modern sink setup with cutting boards and fresh produce."
}, {
  id: "kitchen38",
  title: "White Kitchen with Patterned Backsplash and Black Sink",
  category: "kitchen",
  imageUrl: "/lovable-uploads/93a5c441-2b87-4603-b0cf-36bf1af5c179.png",
  description: "White kitchen with bold circular patterned backsplash, black sink, and decorative wooden cabinets."
}, {
  id: "kitchen39",
  title: "White Kitchen with Large Glass Pendants and Blue Island",
  category: "kitchen",
  imageUrl: "/lovable-uploads/ef310538-30c8-4e92-94eb-7fb27608714a.png",
  description: "Elegant white kitchen with oversized glass pendant lights and deep blue central island."
}, {
  id: "kitchen40",
  title: "Dining Area View from Kitchen with Large Chandelier",
  category: "kitchen",
  imageUrl: "/lovable-uploads/892e4f42-79ed-4c7a-9d24-72218c98f95e.png",
  description: "View from kitchen into dining area featuring a rustic wood table, black chairs, and a statement chandelier."
},
// Bathroom photos
{
  id: "bathroom1",
  title: "Modern White Standalone Bathtub",
  category: "bathroom",
  imageUrl: "/lovable-uploads/db7ccb65-cc95-4071-9da1-5016b506dda5.png",
  description: "Modern white standalone bathtub with large windows and floating vanity"
}, {
  id: "bathroom2",
  title: "Blue Accent Wall Modern Tub",
  category: "bathroom",
  imageUrl: "/lovable-uploads/845d82cf-561d-47eb-8b3b-41f4d5a2721f.png",
  description: "Modern bathroom with white tub, blue accent wall, and large artwork"
}, {
  id: "bathroom3",
  title: "Forest Green Free-Standing Tub",
  category: "bathroom",
  imageUrl: "/lovable-uploads/de97007e-19a1-45bd-a579-d1fb1935b282.png",
  description: "Contemporary bathroom with green walls and free-standing tub"
}, {
  id: "bathroom4",
  title: "Double Sink LED Mirror",
  category: "bathroom",
  imageUrl: "/lovable-uploads/956cb615-f8e9-440f-9461-90c28a214cb1.png",
  description: "Double vanity with illuminated mirrors and light gray tile walls"
}, {
  id: "bathroom5",
  title: "Barn Door Entry",
  category: "bathroom",
  imageUrl: "/lovable-uploads/0352cc6c-9dd2-4b3d-8a73-39dc2d8dfacc.png",
  description: "Rustic barn door entry revealing double sink vanity in bathroom"
}, {
  id: "bathroom6",
  title: "Marble Shower Tub Combo",
  category: "bathroom",
  imageUrl: "/lovable-uploads/98f5e911-30cf-4fe9-b983-e185f1b88cbc.png",
  description: "Luxury bathroom with marble shower and dual rainfall showerheads"
}, {
  id: "bathroom7",
  title: "Mint Green Vanity Dual Sink",
  category: "bathroom",
  imageUrl: "/lovable-uploads/f7a5ce2a-d161-4d3f-9ad1-87691f26c9ee.png",
  description: "Elegant bathroom with mint green vanity, gold mirrors, and classic clawfoot tub"
}, {
  id: "bathroom8",
  title: "Glass Shower Wide Vanity",
  category: "bathroom",
  imageUrl: "/lovable-uploads/0a60f49b-a0b6-4dd4-9257-d06f9428f393.png",
  description: "Three-mirror vanity bathroom with frameless glass shower and LED mirrors"
}, {
  id: "bathroom9",
  title: "Glass Shower Light Tile",
  category: "bathroom",
  imageUrl: "/lovable-uploads/c78943aa-cd0c-46da-b143-98518cfa9123.png",
  description: "Spacious walk-in shower with bench and light stone tile"
}, {
  id: "bathroom10",
  title: "Dual Sink Dark Vanity",
  category: "bathroom",
  imageUrl: "/lovable-uploads/d42d3f0d-348e-42b8-a344-1d5e66803751.png",
  description: "Contemporary bathroom with dual square sinks and espresso vanity cabinets"
}, {
  id: "bathroom11",
  title: "Modern Freestanding Tub White Marble Floor",
  category: "bathroom",
  imageUrl: "/lovable-uploads/0fb32522-99b0-4ad7-8f82-6664dad62226.png",
  description: "Minimalist bathroom with a modern freestanding white tub and marble flooring"
}, {
  id: "bathroom12",
  title: "Dark Green Tub Wood Vanity Modern Style",
  category: "bathroom",
  imageUrl: "/lovable-uploads/239e42be-12a7-40f7-a818-a48c7913f773.png",
  description: "Dark green bathroom with a matching tub and wood vanity featuring lush greenery"
}, {
  id: "bathroom13",
  title: "Light Green Vanity Gold Mirrors Clawfoot Tub",
  category: "bathroom",
  imageUrl: "/lovable-uploads/941dc7ab-e50e-40d9-9a08-50a61a190aec.png",
  description: "Elegant mint green bathroom with double vanity, gold framed mirrors, and clawfoot tub"
}, {
  id: "bathroom14",
  title: "Marble Wall Shower Gold Fixtures",
  category: "bathroom",
  imageUrl: "/lovable-uploads/0dd4435a-b61c-4bfd-a2d4-041d6b63acd2.png",
  description: "Contemporary shower space with marble walls and gold hardware fixtures"
}, {
  id: "bathroom15",
  title: "Blue Accent Wall Freestanding Tub Modern Decor",
  category: "bathroom",
  imageUrl: "/lovable-uploads/d190109a-7dc1-4b23-8635-5adfeda9189c.png",
  description: "Freestanding tub with a bold blue accent wall and large abstract art above"
}, {
  id: "bathroom16",
  title: "Dual Vanity LED Mirrors Glass Shower",
  category: "bathroom",
  imageUrl: "/lovable-uploads/5adb665b-6c60-4313-9f2e-d33a304fc733.png",
  description: "Modern dual-sink vanity with illuminated mirrors and frameless glass shower"
}, {
  id: "bathroom17",
  title: "Glass Shower Marble Tile Modern",
  category: "bathroom",
  imageUrl: "/lovable-uploads/3fb3fa73-c067-44ee-95c8-9badb5aa2cb0.png",
  description: "Walk-in shower with marble tile and built-in niche, adjacent to large vanity"
}, {
  id: "bathroom18",
  title: "Modern Barn Door Dark Wood Vanity",
  category: "bathroom",
  imageUrl: "/lovable-uploads/ee7f6f8e-5c5c-41e1-95c3-a1b603dd3e14.png",
  description: "Bathroom entry with sliding barn door opening to a rustic vanity area"
}, {
  id: "bathroom19",
  title: "Dual Sink Vanity White Subway Tile",
  category: "bathroom",
  imageUrl: "/lovable-uploads/919829fe-7fb7-4a94-8221-7785d11d106b.png",
  description: "Bright dual-sink vanity with black hardware and white subway tile backsplash"
}, {
  id: "bathroom20",
  title: "Modern Gold Trim Bathtub Tile Wall",
  category: "bathroom",
  imageUrl: "/lovable-uploads/03457a36-4820-432e-ba9a-f266a52deb80.png",
  description: "Luxury bathtub with gold fixtures and floral tile pattern wall accent"
}, {
  id: "bathroom21",
  title: "Bathroom White Wall Light Wood Vanity Hex Mirror",
  category: "bathroom",
  imageUrl: "/lovable-uploads/06cbf47a-80c2-4758-abf2-f352310e1e27.png",
  description: "Minimalist bathroom with a light wood vanity, hexagonal mirror, and vertical mosaic tile backsplash."
}, {
  id: "bathroom22",
  title: "Bathroom Light Wood Vanity Floral Tile Floor",
  category: "bathroom",
  imageUrl: "/lovable-uploads/f1ee991c-9b8d-494d-abd2-cd7b5e90e3eb.png",
  description: "Close-up of a bathroom vanity with light wood cabinet and floral-patterned tile flooring."
}, {
  id: "bathroom23",
  title: "Bathroom Dark Wood Vanity Glass Shower",
  category: "bathroom",
  imageUrl: "/lovable-uploads/b79f8ff7-17bb-4a56-a4cc-e228253aee81.png",
  description: "Spacious bathroom featuring a dark wood vanity and glass shower enclosure."
}, {
  id: "bathroom24",
  title: "Bathroom Double Mirror Dark Wood Cabinet",
  category: "bathroom",
  imageUrl: "/lovable-uploads/186a1bb7-424f-44b3-b307-50b199be5203.png",
  description: "Double mirror bathroom with dark wood cabinetry and white countertop."
}, {
  id: "bathroom25",
  title: "Bathroom Gold Shower White Marble",
  category: "bathroom",
  imageUrl: "/lovable-uploads/a7d99e65-7f3d-4d5e-b129-6a9c14a6ce1b.png",
  description: "Elegant white marble shower with built-in bench, gold fixtures, and geometric tile design."
}, {
  id: "bathroom26",
  title: "Bathroom Marble Niche Inset Gold Trim",
  category: "bathroom",
  imageUrl: "/lovable-uploads/9eef1d33-f9cf-4de7-9236-a21371286ab3.png",
  description: "Close-up of marble shower niche with gold trim and hexagonal tile pattern."
}, {
  id: "bathroom27",
  title: "Bathroom Light Grey Cabinet Blue Accent Towels",
  category: "bathroom",
  imageUrl: "/lovable-uploads/bc391cd1-a87c-4126-b5f6-f8fa66766028.png",
  description: "Bright bathroom with grey cabinets, long sink, and blue striped towels."
}, {
  id: "bathroom28",
  title: "Bathroom Closeup White Counter Gold Mirrors",
  category: "bathroom",
  imageUrl: "/lovable-uploads/4106e8c9-e70b-4810-93a1-8c92543acb67.png",
  description: "Modern bathroom countertop featuring rectangular vessel sink and gold-framed mirrors."
}, {
  id: "bathroom29",
  title: "Bathroom Vanity Floor Detail Decorative Tile",
  category: "bathroom",
  imageUrl: "/lovable-uploads/2a0be80e-b3ae-4385-9df9-cb0861b9a4f6.png",
  description: "Low angle of bathroom vanity with patterned floor tiles and decorative handles."
}, {
  id: "bathroom30",
  title: "Bathroom Single Vanity Gold Fixtures Round Mirror",
  category: "bathroom",
  imageUrl: "/lovable-uploads/1250cac2-c8a8-4174-b23b-e94718ee5083.png",
  description: "Modern white bathroom with round mirror, single vanity, and gold plumbing fixtures."
}, {
  id: "bathroom31",
  title: "Elegant White Double Vanity",
  category: "bathroom",
  imageUrl: "/lovable-uploads/dc21e35a-a13c-4434-8b93-9f75311b89da.png",
  description: "Elegant white bathroom with dual vanity sinks and scalloped mirrors."
}, {
  id: "bathroom32",
  title: "Aqua Tone Spa Shower",
  category: "bathroom",
  imageUrl: "/lovable-uploads/57777aef-eb1c-416a-b365-75ed9c17e871.png",
  description: "Spa-style bathroom with aqua wall tones and glass enclosed stone shower."
}, {
  id: "bathroom33",
  title: "Modern Chrome Vessel Sinks",
  category: "bathroom",
  imageUrl: "/lovable-uploads/a2933fad-0dd0-41d5-9038-c7050545d5d5.png",
  description: "Modern vanity with twin vessel sinks and chrome faucets."
}, {
  id: "bathroom34",
  title: "Wood Vanity With Mirror",
  category: "bathroom",
  imageUrl: "/lovable-uploads/b6d10a01-5dbd-4121-acb8-d00fec6f20f7.png",
  description: "Minimalist bathroom featuring wood cabinet and framed mirror."
}, {
  id: "bathroom35",
  title: "Stylish Spa Retreat Vanity",
  category: "bathroom",
  imageUrl: "/lovable-uploads/62b7c237-9a61-442d-8f90-1e9b6bd08dcb.png",
  description: "Spa-inspired vanity setup with aqua walls and wood drawers."
}, {
  id: "bathroom36",
  title: "Wall Mounted Vanity And Toilet",
  category: "bathroom",
  imageUrl: "/lovable-uploads/225838a5-24b2-455f-bb19-882152915e18.png",
  description: "Compact bathroom with floating vanity, marble countertop, and gold fixtures."
}, {
  id: "bathroom37",
  title: "Luxury Marble Shower Tub",
  category: "bathroom",
  imageUrl: "/lovable-uploads/9307917b-878a-4db9-ae1e-fd0b39ac87e1.png",
  description: "Luxurious bathroom with marble shower and freestanding bathtub."
}, {
  id: "bathroom38",
  title: "Spa Style Marble Tiles",
  category: "bathroom",
  imageUrl: "/lovable-uploads/8e320dad-6722-4379-b98c-3c16766f3bd8.png",
  description: "Detailed marble tilework with hex pattern in shower enclosure."
}, {
  id: "bathroom39",
  title: "Modern Gold Tiled Shower",
  category: "bathroom",
  imageUrl: "/lovable-uploads/09f2b73d-3c65-43fd-b7a1-19a5aa8f4bbb.png",
  description: "Contemporary shower design with geometric black and white tiles."
}, {
  id: "bathroom40",
  title: "Modern Bathroom Vanity Double Sink Marble Counter",
  category: "bathroom",
  imageUrl: "/lovable-uploads/5dd7857a-1f7d-4c1f-a202-7384e0c63533.png",
  description: "Modern bathroom vanity with double sink and marble countertop under dual mirrors."
}, {
  id: "bathroom41",
  title: "Luxury Walk-in Shower with Bench and Skylight",
  category: "bathroom",
  imageUrl: "/lovable-uploads/d7a242eb-cfb8-4897-ba77-1d1ee4479158.png",
  description: "Luxurious walk-in shower featuring a granite wall, built-in bench, and skylight."
}, {
  id: "bathroom42",
  title: "Chrome Faucet Marble Sink Closeup",
  category: "bathroom",
  imageUrl: "/lovable-uploads/7e264836-bf9e-45bb-9be9-1795369b92a2.png",
  description: "Close-up of a chrome faucet with running water into a rectangular marble sink."
}, {
  id: "bathroom43",
  title: "Double Mirror Marble Countertop Bathroom Vanity",
  category: "bathroom",
  imageUrl: "/lovable-uploads/9d30ed06-af99-4a1d-b10a-bc0f3db3d7bd.png",
  description: "Double vanity with marble countertop and twin mirrors in a contemporary bathroom."
}, {
  id: "bathroom44",
  title: "Freestanding Tub Under Frosted Window",
  category: "bathroom",
  imageUrl: "/lovable-uploads/0b8f05f0-c562-4dc2-906f-07f942e98dc1.png",
  description: "Freestanding bathtub positioned under a frosted privacy window in a tiled bathroom."
}, {
  id: "bathroom45",
  title: "Floating Vanity Golden Shower Cube Tiles",
  category: "bathroom",
  imageUrl: "/lovable-uploads/45872fdd-559b-4b5d-ac9b-348a741ea326.png",
  description: "Floating wood vanity with gold fixtures and cube-patterned shower tiles in a luxury bathroom."
}, {
  id: "bathroom46",
  title: "Geometric Shower Tile Glass Door Design",
  category: "bathroom",
  imageUrl: "/lovable-uploads/83eb5a15-ce54-43b4-a8ee-2daf631b8d59.png",
  description: "Geometric cube-tiled shower wall with gold trim and frameless glass door."
}, {
  id: "bathroom47",
  title: "Hexagonal Tiles Cube Shower Wall",
  category: "bathroom",
  imageUrl: "/lovable-uploads/4e40a969-e05b-455c-ba6e-3a733ed7523c.png",
  description: "Hexagon and cube-pattern tile contrast in a modern glass-enclosed shower."
}, {
  id: "bathroom48",
  title: "Gold Shower Fixture Cube Tile Wall",
  category: "bathroom",
  imageUrl: "/lovable-uploads/f236a33d-7e64-4f91-8a4c-a5e85d5b8279.png",
  description: "Gold shower fixture against a 3D cube pattern tile wall in a luxury shower."
}, {
  id: "bathroom49",
  title: "Tropical Bathroom Wood Vanity Nature View",
  category: "bathroom",
  imageUrl: "/lovable-uploads/87194642-4ca3-4614-8b67-0e5bc73e9fe0.png",
  description: "Bathroom with wooden vanity and tropical garden view through large window."
},
// LA Wee Model photos
{
  id: "lawee1",
  title: "Modern Wood and White Flat Roof Design",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/f2f75418-221d-4d53-96b3-19e60e4daf31.png",
  description: "Our LA Wee by UDA ADU blends modern minimalism with vertical cedar siding on the left and smooth white stucco on the right, topped by a flat roof and black-framed windows under a deep entry canopy."
}, {
  id: "lawee2",
  title: "Modern Flat Roof with Orange Door",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/3835be90-8f7d-4200-abcf-72edd9899d2b.png",
  description: "This LA Wee ADU from UDA showcases a sleek flat-roof form with alternating white stucco and vertical cedar, punctuated by an orange entry door and sharp black window detailing."
}, {
  id: "lawee3",
  title: "Contemporary Wood and White Facade",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/3d8d3d55-326c-4d94-8510-00d291374496.png",
  description: "Our LA Wee unit delivers a modern yet warm aesthetic, using vertical cedar wood, white stucco, and a flat black canopy to define its balanced front facade and clean lines."
}, {
  id: "lawee4",
  title: "Minimalist White Corrugated Siding",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/c2d13aed-bf67-4fee-a394-e4e00b9be6c1.png",
  description: "Designed as part of UDA's LA Wee lineup, this modern flat-roof ADU features white board-and-batten siding, black framed windows, and a central black entry door with a metal awning."
}, {
  id: "lawee5",
  title: "Warm Wood Tone Flat Roof Home",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/646925b9-3161-447e-93b9-fab46768eb47.png",
  description: "This UDA ADU model channels a bold contemporary look with full-height vertical cedar cladding, a flat roof, and modern black windows flanking a central entry with deep overhang."
}, {
  id: "lawee6",
  title: "Modern Wood and Stucco Combination",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/b4a0fef4-e7b7-4b2b-99a0-d01907787e1a.png",
  description: "Our compact LA Wee plan brings color and warmth through white stucco paired with horizontal cedar cladding, a flat roofline, and a bold orange door framed by black-trimmed windows."
}, {
  id: "lawee7",
  title: "Contemporary White and Orange Accent",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/ea20a48b-2f23-411d-854b-b2ddd5c05acb.png",
  description: "UDA's modern LA Wee design contrasts white stucco with a vibrant vertical orange-toned wood section, all under a flat roof with black windows and a statement orange front door."
}, {
  id: "lawee8",
  title: "Farmhouse Style with Orange Door",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/55ef0f4b-c8f8-4e86-8a62-092d3f52ef7d.png",
  description: "This ADU from the LA Wee series features timeless farmhouse-inspired styling with board-and-batten white siding, a gable roof in black standing seam metal, and a bright orange door that adds a punch of personality."
}, {
  id: "lawee9",
  title: "Classic Farmhouse White Siding",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/84368cbf-ccd3-4dba-83a0-3ce48ebee7cb.png",
  description: "Our LA Wee gable model by UDA ADU leans classic with its all-white board-and-batten exterior, warm wood front door, symmetrical windows, and standing seam metal roof for a fresh yet traditional aesthetic."
}, {
  id: "lawee10",
  title: "Traditional Style with Arched Windows",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/06b68376-e436-416b-a3c2-069e6583d19b.png",
  description: "With arched windows and a red-toned arched front door, this LA Wee model brings a modern Spanish touch to the UDA ADU series, blending white stucco with a dark metal roof and curved architectural charm."
}, {
  id: "lawee11",
  title: "Mediterranean Style Traditional Roof",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/96bdae4f-6c05-400f-9b63-0ad04f9d51aa.png",
  description: "Part of UDA's Mediterranean-inspired LA Wee designs, this ADU uses white stucco, a dark tile gable roof, black arched windows, and a bold red arched entry door to evoke timeless coastal elegance."
}, {
  id: "lawee12",
  title: "Contemporary Mixed Material Design",
  category: "los-altos-1",
  imageUrl: "/lovable-uploads/24dab373-5fb5-48f7-973e-4a71e458f4c6.png",
  description: "Our LA Wee ADU model stands out with a moody modern-craftsman feel — charcoal brick siding, black standing seam metal roof, warm wood door, and soft lighting create a cozy, contemporary character."
}, {
  id: "lamedian1",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/f397b8de-0a38-41eb-ae11-0f2450512870.png",
  description: "Our LA Median model highlights West Coast minimalism with smooth white stucco, natural wood accents, a cantilevered flat roof canopy, and warm wood-framed windows that create a serene, modern retreat."
}, {
  id: "lamedian2",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/097bfbcf-1116-4baf-868a-31d560f97901.png",
  description: "This UDA LA Median ADU pairs creamy stucco with a bold stacked stone centerpiece and vertical cedar detailing, offering a refined modern facade under a clean flat roof."
}, {
  id: "lamedian3",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/1f8eabb7-4866-4166-bf46-7dd8aa9652dc.png",
  description: "Our LA Median flat-roof model introduces Spanish-modern flair with arched black-trimmed windows, white stucco, a rich wood front door, and modern lighting under a streamlined black canopy."
}, {
  id: "lamedian4",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/1bcb4ac0-58fa-44e8-b811-0f7772a70c18.png",
  description: "This modern LA Median ADU by UDA uses bold vertical metal siding, warm cedar cladding, and black-framed windows, all under a crisp flat roof for a dramatic and contemporary street presence."
}, {
  id: "lamedian5",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/dc47d05b-7786-4818-8d70-0f3ca000eb60.png",
  description: "Our LA Median model pairs clean white board-and-batten siding with a bold black standing seam gable roof and a natural wood front door — a modern farmhouse silhouette at 854 sq ft from UDA ADU."
}, {
  id: "lamedian6",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/424edfe3-53a6-4ffe-8180-fa0c8e71cbb4.png",
  description: "The 854 sq ft LA Median model brings refined farmhouse charm with vertical white siding, black framed grid windows, a black standing seam roof, and symmetrical front gables for timeless curb presence."
}, {
  id: "lamedian7",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/64b97f4e-b679-470d-9eee-6c24c2d691e7.png",
  description: "Our 854 sq ft LA Median design embraces cottage-inspired detail with gray shutters, board-and-batten siding, and a classic shingle roof, all centered around a wood-toned entry and crisp landscaping."
}, {
  id: "lamedian8",
  title: "",
  category: "los-altos-2",
  imageUrl: "/lovable-uploads/ba410875-f7b9-46e9-962f-188f194e3704.png",
  description: "This Mediterranean-style LA Median model from UDA spans 854 sq ft and showcases warm stucco walls, a classic red tile roof, dark wood accents, and clean desert-style landscaping."
}, {
  id: "lagrand1",
  title: "",
  category: "los-altos-3",
  imageUrl: "/lovable-uploads/45b304df-40d0-41b8-bdb3-ea9380187de2.png",
  description: "This LA Grand model showcases contemporary ranch styling with white board-and-batten siding and warm wood shutters. The design features a sleek gray roof and modern landscaping that complements the 1190 sq ft layout."
}, {
  id: "lagrand2",
  title: "",
  category: "los-altos-3",
  imageUrl: "/lovable-uploads/1bb5d97a-408e-495c-aca5-84f69f2cfb44.png",
  description: "Our LA Grand design emphasizes clean lines with vertical white siding and a natural wood entry door. The gabled roofline and thoughtful window placement create an inviting facade for this spacious 2-bedroom, 2-bathroom ADU."
}, {
  id: "lagrand3",
  title: "",
  category: "los-altos-3",
  imageUrl: "/lovable-uploads/f304cfa8-d6e1-4f13-9d18-785b1b4cdbb1.png",
  description: "This striking LA Grand model combines bold black metal siding with crisp white accents and warm wood details. The standing seam roof and large windows create a dramatic contrast while maximizing natural light throughout the 1190 sq ft interior."
}, {
  id: "lagrand4",
  title: "",
  category: "los-altos-3",
  imageUrl: "/lovable-uploads/941dc666-4b91-4841-a540-4095ba5efd64.png",
  description: "The LA Grand model embraces Mediterranean charm with warm stucco walls and a classic red tile roof. Decorative shutters and traditional architectural details create timeless appeal for this generous 2-bedroom, 2-bathroom layout."
}, {
  id: "gilmour1",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/4c3e1549-60dd-47ed-af3f-27a550722fe9.png",
  description: "This modern Gilmour studio features crisp white horizontal siding with black trim and a striking warm wood entry door. The sleek flat roof and large picture windows create a contemporary aesthetic perfect for compact living."
}, {
  id: "gilmour2",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/f81f8254-59e6-4d6f-ad67-f2d1eefd6ab6.png",
  description: "The Gilmour model showcases beautiful light-colored brick exterior with a sophisticated flat roof design. Natural wood accents and black-framed windows add warmth to this efficient 378 sq ft studio layout."
}, {
  id: "gilmour3",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/38323e8d-106f-40cf-82bb-e97114d4b2eb.png",
  description: "This minimalist Gilmour design features smooth stucco walls in warm beige tones with a sleek black roof. The natural wood entry door and black window frames create elegant contrast in this compact studio ADU."
}, {
  id: "gilmour4",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/1c5eda3d-2a0e-4071-86d6-2ad3f38d8c25.png",
  description: "The Gilmour model embraces modern simplicity with clean stucco walls and a distinctive sloped black roof. Warm wood entry and black window trim complement the understated elegance of this efficient studio space."
}, {
  id: "gilmour5",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/0d215b87-6d89-49a9-9610-cd01b67172a5.png",
  description: "This Mediterranean-inspired Gilmour features classic stucco walls with a beautiful terracotta tile roof. The arched entry door and traditional detailing bring Old World charm to this compact 378 sq ft studio design."
}, {
  id: "gilmour6",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/0214472a-e1bc-4e27-abeb-03bebbb2b547.png",
  description: "The Gilmour model showcases modern farmhouse style with white vertical siding and a dramatic black standing seam roof. Natural wood entry door and black trim create a fresh, contemporary look for efficient studio living."
}, {
  id: "gilmour7",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/2d3acf6b-89f1-4b15-8377-9a218325da1a.png",
  description: "This elegant Gilmour design features smooth white stucco walls with a sleek black roof for a timeless modern aesthetic. The warm wood entry door and perfectly proportioned windows maximize natural light in this compact studio space."
}, {
  id: "gilmour8",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/5f2fc97d-5bfd-41ea-9ff7-b2941851c427.png",
  description: "The Gilmour model showcases rich natural wood siding with traditional craftsmanship details and a classic black roof. Stone accents and thoughtful window placement create a warm, inviting facade for this efficient studio ADU."
}, {
  id: "gilmour9",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/60cf96a6-340d-4974-a736-c8a8a48e0b36.png",
  description: "This sophisticated Gilmour features light gray horizontal siding with a sleek black roof and natural wood accents. The clean lines and modern proportions make this 378 sq ft studio both stylish and functional."
}, {
  id: "gilmour10",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/8037a6c9-118a-41c7-b649-0627a1676a70.png",
  description: "The Gilmour model presents a contemporary design with smooth beige stucco walls and a distinctive corrugated metal roof. Modern black-framed windows and warm wood entry create an inviting entrance to this efficient studio layout."
}, {
  id: "gilmour11",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/f344617b-9bdc-4e5c-af34-b51c5c06e8ed.png",
  description: "This clean Gilmour design features white vertical board-and-batten siding with a sleek metal standing seam roof. The warm wood entry door and large windows create an inviting and modern studio living space."
}, {
  id: "gilmour12",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/bee154bb-0eac-4d54-9dde-140f1c433b15.png",
  description: "The Gilmour model showcases a beautiful combination of smooth white stucco and natural cedar siding with a dark hip roof. The warm wood entry and large picture window create perfect harmony between modern and natural elements."
}, {
  id: "gilmour13",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/d37a4e0f-ff13-4637-9ae8-9d415b854bb1.png",
  description: "This sophisticated Gilmour features light brick exterior walls with a sleek corrugated metal roof and dark window frames. The clean lines and modern proportions make this studio ADU both elegant and functional."
}, {
  id: "gilmour14",
  title: "",
  category: "gilmour",
  imageUrl: "/lovable-uploads/d06b39f5-a1d0-4847-b49b-3793d7e60890.png",
  description: "The Gilmour model embraces contemporary style with light gray brick walls and a modern standing seam roof. Natural wood entry door and black-framed windows create striking contrast in this efficient studio design."
}, {
  id: "dimeola1",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/7ee44235-f564-4f87-bee6-7bfb3326e99d.png",
  description: "The Di Meola model features sleek white stucco walls with black-framed windows and a distinctive black entry door with horizontal slats. The modern flat roof and symmetrical design create a bold contemporary statement perfect for this 1-bedroom, 1-bathroom ADU."
}, {
  id: "dimeola2",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/1151187d-3584-4f64-9732-dfb8d252dae3.png",
  description: "This Di Meola design showcases elegant white horizontal siding with a stunning dark standing seam roof. The warm wood entry door and black window frames create perfect contrast in this 389 sq ft layout with beautiful professional landscaping."
}, {
  id: "dimeola3",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/91d65867-5dc5-4ce8-9e43-2d5241f6d879.png",
  description: "The Di Meola model embraces Mediterranean charm with smooth sage green stucco walls and classic terracotta clay tile roof. Arched windows and entry door with natural stone accents bring timeless elegance to this efficient 1-bedroom ADU."
}, {
  id: "dimeola4",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/2a7d743b-b1df-42da-8733-9e5659033ad8.png",
  description: "This sophisticated Di Meola features mixed materials with smooth white stucco and natural stone accent wall. The modern standing seam roof and large picture windows maximize natural light in this stylish 389 sq ft living space."
}, {
  id: "dimeola5",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/47364701-50b4-4359-84d0-ee15c3a6f51b.png",
  description: "The Di Meola model showcases contemporary minimalism with crisp white stucco walls and sleek black window frames. The flat roof design and clean lines create a striking modern aesthetic for this efficient 1-bedroom, 1-bathroom layout."
}, {
  id: "dimeola6",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/57eff732-cd67-45e9-82c6-7811fa9d8929.png",
  description: "This stunning Di Meola design features beautiful natural cedar horizontal siding with black window trim and a modern standing seam roof. The warm wood tones and contemporary proportions create an inviting entrance to this 389 sq ft ADU."
}, {
  id: "dimeola7",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/4fcc4a85-a5ec-4844-806c-a0258a10064c.png",
  description: "The Di Meola model combines natural cedar siding with smooth white stucco for a modern mixed-material facade. The standing seam roof and thoughtfully placed windows create perfect balance in this efficient 1-bedroom design."
}, {
  id: "dimeola8",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/f5f43e31-9db8-494f-a880-a7249148585d.png",
  description: "This contemporary Di Meola features charcoal blue horizontal siding with crisp white trim and a modern standing seam roof. The warm wood entry door and perfectly proportioned windows create a welcoming facade for this 389 sq ft layout."
}, {
  id: "dimeola9",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/b64bf7f7-9f49-4a05-817b-628afc76e304.png",
  description: "The Di Meola model showcases sophisticated blue-gray horizontal siding with white trim details and a sleek metal roof. Large picture windows and thoughtful design proportions maximize both natural light and curb appeal in this 1-bedroom ADU."
}, {
  id: "dimeola10",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/fdf8cbc0-2f96-49c2-bd4b-08d38a10a783.png",
  description: "This striking Di Meola design combines natural cedar vertical siding with modern blue-gray stucco walls. The sleek standing seam roof and black-framed windows create perfect contrast in this contemporary 389 sq ft 1-bedroom layout."
}, {
  id: "dimeola11",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/22740e35-8803-43d1-9469-eca98a869185.png",
  description: "The Di Meola model features beautiful navy blue vertical siding with warm cedar wood trim and a matching standing seam roof. The elevated wood deck entry and large picture windows create an inviting entrance to this efficient 1-bedroom ADU."
}, {
  id: "dimeola12",
  title: "",
  category: "di-meola",
  imageUrl: "/lovable-uploads/c8ad1383-371c-440b-a2e1-556399f6568e.png",
  description: "This sophisticated Di Meola showcases a perfect blend of natural cedar vertical siding and bold navy blue stucco with white window trim. The modern standing seam roof and elevated wood entry steps complete this stylish 389 sq ft design."
}, {
  id: "satriani1",
  title: "",
  category: "satriani",
  imageUrl: "/lovable-uploads/9d75f844-c186-4770-9675-b839797350e0.png",
  description: "The Satriani model features beautiful natural cedar vertical siding with black-framed windows and a modern standing seam roof. This design creates a warm yet contemporary aesthetic perfect for any backyard setting."
}, {
  id: "satriani2",
  title: "",
  category: "satriani",
  imageUrl: "/lovable-uploads/4e92b4c1-bdaf-4f5e-b287-7e6767768c09.png",
  description: "This Satriani showcases clean white stucco walls with natural wood entry door and black window frames. The modern single-pitch roof design and minimalist landscaping create an elegant contemporary ADU."
}, {
  id: "satriani3",
  title: "",
  category: "satriani",
  imageUrl: "/lovable-uploads/30d7ffee-4003-44f9-b688-5f1fa676406d.png",
  description: "The Satriani model combines natural weathered wood siding with gray brick wainscoting and a dramatic single-pitch roof. The warm wood entry door and black window frames complete this sophisticated design."
}, {
  id: "satriani4",
  title: "",
  category: "satriani",
  imageUrl: "/lovable-uploads/df2460e8-8ffa-44f4-a88a-5563c68d695d.png",
  description: "This striking Satriani features charcoal gray horizontal siding with natural cedar upper section and a modern single-pitch roof. The white-framed windows and classic wood entry door create perfect contrast."
}, {
  id: "satriani5",
  title: "",
  category: "satriani",
  imageUrl: "/lovable-uploads/d1c60978-91ae-4bc2-b832-be38e6f7844a.png",
  description: "The Satriani model showcases warm cedar horizontal siding with natural stone wainscoting and a contemporary single-pitch roof. Large picture windows and traditional wood entry door highlight this 541 sq ft design."
}, {
  id: "satriani6",
  title: "",
  category: "satriani",
  imageUrl: "/lovable-uploads/2066a895-fe8d-472a-a15a-119e067b41b6.png",
  description: "This elegant Satriani features smooth white stucco walls with a classic hip roof design and black window frames. The natural wood entry door and thoughtful landscaping create a timeless aesthetic."
}, {
  id: "knopfler1",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/a3dc01c1-355e-4bc3-995a-253f7082362b.png",
  description: "The Knopfler model showcases a stunning contemporary farmhouse design with crisp white stucco walls and a dramatic dark slate roof. Featuring dormer windows, natural wood entry door, black window frames, and modern landscape lighting, this 797 sq ft ADU combines traditional charm with modern sophistication."
}, {
  id: "knopfler2",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/0c7fe542-ca5b-48ac-9ad7-116907e12cea.png",
  description: "Modern farmhouse design featuring white stucco walls with natural wood accents and a metal roof with dormer windows."
}, {
  id: "knopfler3",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/985f83d6-f5ad-473a-9806-d66813a740e7.png",
  description: "Contemporary ADU with standing seam metal roof, white stucco exterior, and natural wood siding accents."
}, {
  id: "knopfler4",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/82c60945-85be-45ae-9b64-2c6b7df226b1.png",
  description: "Elegant single-story design with white stucco walls, dark wood accents, and large picture windows."
}, {
  id: "knopfler5",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/0c964645-fb4f-4ab6-bef4-f4804b6c5bbf.png",
  description: "Classic farmhouse style with white vertical siding, metal roof, and natural stone foundation accents."
}, {
  id: "knopfler6",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/8f6870ea-a4a1-4ecc-9d2c-e765d326e290.png",
  description: "Traditional design with textured stucco walls, clay tile roof, and arched entry door."
}, {
  id: "knopfler7",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/8dc47923-a4a1-4073-8076-20b1defecc8b.png",
  description: "Dark exterior finish with dramatic clay tile roof, arched windows, and warm interior lighting."
}, {
  id: "knopfler8",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/3efa0bb1-cf8e-4cba-900c-b9398682cd3a.png",
  description: "Light stucco exterior with clay tile roof, arched windows, and symmetrical design."
}, {
  id: "knopfler9",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/932fc8c1-1c27-4a81-980f-8577128b26d3.png",
  description: "Contemporary design with white stucco walls, standing seam metal roof, and large arched windows."
}, {
  id: "knopfler10",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/9e8702a3-e6bc-46fa-8471-29d45d9a07ca.png",
  description: "Modern farmhouse with white stucco exterior, metal roof, and dramatic arched windows with black frames."
}, {
  id: "knopfler11",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/1e9d4253-e6bf-442c-aae2-8dd85561e55f.png",
  description: "Stunning contemporary home with vibrant blue stucco exterior, arched windows, and a bold orange entry door creating a striking visual contrast."
}, {
  id: "knopfler12",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/9f6d9d10-9e4c-47e7-8589-6de98a73a3be.png",
  description: "Modern farmhouse design featuring dark metal siding, red wood accents, standing seam metal roof, and contemporary large windows."
}, {
  id: "knopfler13",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/82b55680-c960-46a8-af9b-7288cf93ee80.png",
  description: "Contemporary single-story home with natural stone foundation, light wood siding, dark metal roof, and warm wood entry door."
}, {
  id: "knopfler14",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/9b280937-5667-4f82-9bec-256d5c1e9a28.png",
  description: "Elegant modern design with cream stucco walls, natural stone wainscoting, dark metal roof, and beautiful wood entry door."
}, {
  id: "knopfler15",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/78675f2f-669a-4506-a087-f7a64611e202.png",
  description: "Contemporary home featuring dark vertical siding, cream stucco accents, stone foundation, and warm wood entry with modern black fixtures."
}, {
  id: "knopfler16",
  title: "",
  category: "knopfler",
  imageUrl: "/lovable-uploads/afa73d3f-795a-45b0-806f-18274f5e7e79.png",
  description: "Beautiful contemporary home featuring warm wood vertical siding, dark metal roof, stone foundation, and black-framed windows creating a perfect blend of natural materials and modern design."
}];

// Videos for each category
const videos: Video[] = [
// General ADU video
{
  id: "adu-general-video",
  title: "ADU Construction",
  category: "adu",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/sample-video.mp4",
  thumbnailUrl: "/lovable-uploads/ee8bd633-658a-4cbf-bb86-a2b8fb69c5fd.png",
  description: "Overview of our ADU construction process"
},
// Satriani ADU video
{
  id: "satriani-video",
  title: "Satriani ADU",
  category: "satriani",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/Satriani.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvU2F0cmlhbmkubXA0IiwiaWF0IjoxNzUzMzM3NDIxLCJleHAiOjIwNjg2OTc0MjF9.9ZCqDYpSWXIes7b87daFJ9FzhahpxPBreakJxAAlmFY",
  thumbnailUrl: "/lovable-uploads/ee8bd633-658a-4cbf-bb86-a2b8fb69c5fd.png",
  description: "Satriani ADU - 1 Bed, 1 Bath - 541 SF"
},
// House video
{
  id: "con-home-video",
  title: "Con Home",
  category: "house",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/House/Con%20Home%20video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvSG91c2UvQ29uIEhvbWUgdmlkZW8ubXA0IiwiaWF0IjoxNzUzMzQ1NTA2LCJleHAiOjIwNjg3MDU1MDZ9.blY-hO8tmqKrbMCHNOyVDsHOThIfOrDOrjHb8QmgpIc",
  thumbnailUrl: "/lovable-uploads/9ceebd4f-3a0b-4cce-825c-90fb7b8becff.png",
  description: "Con Home construction showcase"
},
// Gilmore specific video
{
  id: "gilmour-video",
  title: "Gilmour Model",
  category: "gilmour",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/Gilmour%20Model%20Video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvR2lsbW91ciBNb2RlbCBWaWRlby5tcDQiLCJpYXQiOjE3NTMzMDEyNjksImV4cCI6MjA2ODY2MTI2OX0.nOYum6KExylRX24fwL6tTK7hDHCUBHLvKnLJEpvzTYo",
  thumbnailUrl: "/lovable-uploads/ee8bd633-658a-4cbf-bb86-a2b8fb69c5fd.png",
  description: "Gilmour model walkthrough"
},
// Di Meola video
{
  id: "di-meola-video",
  title: "Di Meola Model",
  category: "di-meola",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/7-22%20Di%20Meola%20Mode%20video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvNy0yMiBEaSBNZW9sYSBNb2RlIHZpZGVvLm1wNCIsImlhdCI6MTc1MzMwNDEyOSwiZXhwIjoyMDY4NjY0MTI5fQ.TAWs5TrmajJ47g5IFFuLnrK4WDPKf70eP-z3Jm-ZSXs",
  thumbnailUrl: "/lovable-uploads/75108e2e-3bad-4e91-8f94-52420907c290.png",
  description: "Di Meola model overview"
},
// Di Meola video 2
{
  id: "di-meola-video-2",
  title: "Di Meola Model 2",
  category: "di-meola",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/My%20Movie%208%202.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvTXkgTW92aWUgOCAyLm1wNCIsImlhdCI6MTc1MzMwNDkyMSwiZXhwIjoyMDY4NjY0OTIxfQ._mKvyXYQq-s2PE4DhBXTmhR6-XlyYaCPMkA8qf3iG94",
  thumbnailUrl: "/lovable-uploads/75108e2e-3bad-4e91-8f94-52420907c290.png",
  description: "Di Meola model second video"
},
{
  id: "knopfler-video",
  title: "Knopfler Grand Long Video",
  category: "knopfler",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/Knopfler%20Grand%20Long%20Video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvS25vcGZsZXIgR3JhbmQgTG9uZyBWaWRlby5tcDQiLCJpYXQiOjE3NTM0MDk0MjQsImV4cCI6MjA2ODc2OTQyNH0.6YpMhXbUhxHnsrTaB-kLfXzg9A0YuWK1UFxsohnFnAc",
  thumbnailUrl: "/lovable-uploads/a3dc01c1-355e-4bc3-995a-253f7082362b.png",
  description: "Knopfler Grand Long Video - 1 Bed, 1 Bath - 797 SF"
},
// House video
{
  id: "house-video",
  title: "Modern Home",
  category: "house",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/sample-video4.mp4",
  thumbnailUrl: "/lovable-uploads/9ceebd4f-3a0b-4cce-825c-90fb7b8becff.png",
  description: "Modern home construction"
},
// Interior video
{
  id: "interior-video",
  title: "Interior Design",
  category: "interior",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/sample-video5.mp4",
  thumbnailUrl: "/lovable-uploads/55e33f9c-6e09-4ed6-b66c-2764cff17804.png",
  description: "Interior design showcase"
},
// Kitchen video
{
  id: "kitchen-video",
  title: "Kitchen Design",
  category: "kitchen",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/sample-video6.mp4",
  thumbnailUrl: "/lovable-uploads/1ac2dacd-897e-4a2d-8f1c-6a2f2074602f.png",
  description: "Modern kitchen design"
},
// Bathroom video
{
  id: "bathroom-video",
  title: "Bathroom Design",
  category: "bathroom",
  videoUrl: "https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/sample-video7.mp4",
  thumbnailUrl: "/lovable-uploads/76dbc8fc-fa84-46e7-8d0c-b8e57ac32bcd.png",
  description: "Luxury bathroom design"
}];
function OurWorkContent() {
  const [selectedCategory, setSelectedCategory] = useState<string>("adu");
  const [selectedAduSubcategory, setSelectedAduSubcategory] = useState<string | null>(null);
  const [showPhotoGrid, setShowPhotoGrid] = useState<boolean>(false);
  const [hoveredPhoto, setHoveredPhoto] = useState<Photo | null>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const isMobile = useIsMobile();
  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({
      x: e.clientX,
      y: e.clientY
    });
  };
  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === "adu") {
      setSelectedAduSubcategory(null);
      setShowPhotoGrid(false);
    } else {
      setSelectedAduSubcategory(null);
      setShowPhotoGrid(true);
    }
  };
  const handleAduSubcategoryClick = (subcategoryId: string) => {
    setSelectedAduSubcategory(subcategoryId);
    setShowPhotoGrid(true);
  };
  const handlePhotoClick = (photo: Photo, index: number) => {
    setSelectedPhoto(photo);
    setCurrentPhotoIndex(index);
  };
  const handleVideoClick = (video: Video) => {
    setSelectedVideo(video);
  };
  const closePhotoModal = () => {
    setSelectedPhoto(null);
  };
  const closeVideoModal = () => {
    setSelectedVideo(null);
  };
  const nextPhoto = () => {
    const currentPhotos = getCurrentPhotos();
    setCurrentPhotoIndex(prev => (prev + 1) % currentPhotos.length);
    setSelectedPhoto(currentPhotos[(currentPhotoIndex + 1) % currentPhotos.length]);
  };
  const prevPhoto = () => {
    const currentPhotos = getCurrentPhotos();
    setCurrentPhotoIndex(prev => (prev - 1 + currentPhotos.length) % currentPhotos.length);
    setSelectedPhoto(currentPhotos[(currentPhotoIndex - 1 + currentPhotos.length) % currentPhotos.length]);
  };
  const getCurrentPhotos = () => {
    if (selectedCategory === "adu" && selectedAduSubcategory) {
      // Return specific photos for Gilmour model
      if (selectedAduSubcategory === "gilmour") {
        return photos.filter(photo => photo.category === "gilmour");
      }
      // Return specific photos for Di Meola model
      if (selectedAduSubcategory === "di-meola") {
        return photos.filter(photo => photo.category === "di-meola");
      }
      // Return specific photos for Satriani model
      if (selectedAduSubcategory === "satriani") {
        return photos.filter(photo => photo.category === "satriani");
      }
      // Return specific photos for Knopfler model
      if (selectedAduSubcategory === "knopfler") {
        return photos.filter(photo => photo.category === "knopfler");
      }
      // Return specific photos for Moore model
      if (selectedAduSubcategory === "moore") {
        return photos.filter(photo => photo.category === "moore");
      }
      // Return specific photos for LA Wee model
      if (selectedAduSubcategory === "los-altos-1") {
        return photos.filter(photo => photo.category === "los-altos-1");
      }
      // Return specific photos for LA Median model
      if (selectedAduSubcategory === "los-altos-2") {
        return photos.filter(photo => photo.category === "los-altos-2");
      }
      // Return specific photos for LA Grand model
      if (selectedAduSubcategory === "los-altos-3") {
        return photos.filter(photo => photo.category === "los-altos-3");
      }
      // Return ADU photos excluding specific ones for Other Projects
      if (selectedAduSubcategory === "other-projects") {
        return photos.filter(photo => photo.category === "adu" && photo.id !== "adu1" && photo.id !== "adu7");
      }
      // For other subcategories, return ADU photos
      return photos.filter(photo => photo.category === "adu");
    }
    return photos.filter(photo => photo.category === selectedCategory);
  };
  const getCurrentVideos = () => {
    if (selectedCategory === "adu" && selectedAduSubcategory) {
      // Return specific video for each ADU subcategory
      return videos.filter(video => video.category === selectedAduSubcategory);
    }
    // For non-ADU categories, return videos for that category
    return videos.filter(video => video.category === selectedCategory);
  };
  const { theme } = useNeumorphicTheme();
  const isDark = theme === "dark";
  const textColor = isDark ? "text-white" : "text-[#1a1a1a]";
  const raisedButtonStyles = isDark
    ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]'
    : 'bg-white text-[#e4683b] shadow-[6px_6px_12px_#d8d8d8,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#d0d0d0,-8px_-8px_16px_#ffffff]';
  const raisedButtonActiveStyles = isDark
    ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]'
    : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d0d0d0,-8px_-8px_16px_#ffffff]';
  
  return <NeumorphicContainer>
      <CanonicalTag url="https://www.udaadu.com/our-work" />
      <Navigation />
      
      <div className="py-12 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-8 my-0">
          <h1 className="text-4xl font-bold mb-2 text-[#e4683b] mx-0 py-0 my-[8px] md:text-6xl">Our Work</h1>
        </div>

        {/* Photo Ticker for ADU category */}
        {selectedCategory === "adu" && <div className="mb-16 mt-4 w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] overflow-hidden">
            {/* Photo Ticker */}
            <div className="relative h-16 overflow-hidden">
              <div className="flex animate-scroll-right whitespace-nowrap">
                {/* First set of Knopfler photos */}
                {photos.filter(photo => photo.category === "knopfler").map((photo, index) => <div key={`ticker1-${photo.id}`} className="flex-shrink-0 w-[480px] h-16 overflow-hidden">
                    <img src={photo.imageUrl} alt={photo.title} className="w-full h-[200%] object-cover object-center -translate-y-[50%]" />
                  </div>)}
                
                {/* Duplicate set for seamless loop */}
                {photos.filter(photo => photo.category === "knopfler").map((photo, index) => <div key={`ticker2-${photo.id}`} className="flex-shrink-0 w-[480px] h-16 overflow-hidden">
                    <img src={photo.imageUrl} alt={photo.title} className="w-full h-[200%] object-cover object-center -translate-y-[50%]" />
                  </div>)}
                
                {/* Additional duplicates for mobile to prevent gaps */}
                {photos.filter(photo => photo.category === "knopfler").map((photo, index) => <div key={`ticker3-${photo.id}`} className="flex-shrink-0 w-[480px] h-16 overflow-hidden">
                    <img src={photo.imageUrl} alt={photo.title} className="w-full h-[200%] object-cover object-center -translate-y-[50%]" />
                  </div>)}
              </div>
              
              {/* Darker overlay covering entire ticker with white title */}
              <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                <p className="text-white max-w-3xl mx-auto text-3xl font-semibold">Explore our portfolio</p>
              </div>
            </div>
          </div>}

        {/* Main Video Section */}
        <div className="mb-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
              <video
                src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/Knopfler%20Grand%20Long%20Video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvS25vcGZsZXIgR3JhbmQgTG9uZyBWaWRlby5tcDQiLCJpYXQiOjE3NTM0MDk0MjQsImV4cCI6MjA2ODc2OTQyNH0.6YpMhXbUhxHnsrTaB-kLfXzg9A0YuWK1UFxsohnFnAc"
                controls
                autoPlay
                loop
                muted
                className="w-full h-[400px] md:h-[500px] object-cover"
                poster=""
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        {/* Category Selection */}
        <div className="flex flex-wrap justify-center gap-6 mb-12 mt-8">
          {categories.map(category => <button key={category.id} onClick={() => handleCategoryClick(category.id)} className={`px-8 py-4 md:px-8 md:py-4 px-4 py-2 font-medium text-xl md:text-xl text-sm transition-all duration-300 rounded-xl border-0 ${selectedCategory === category.id ? raisedButtonActiveStyles : raisedButtonStyles}`}>
              {category.name}
            </button>)}
        </div>

        {/* ADU Subcategory Selection */}
        {selectedCategory === "adu" && <div className="flex flex-wrap justify-center gap-6 md:gap-6 gap-3 mb-24 px-[27px] mx-0">
            {aduSubcategories.map(subcategory => <button key={subcategory.id} onClick={() => handleAduSubcategoryClick(subcategory.id)} className={`px-6 py-3 md:px-6 md:py-3 px-3 py-2 font-medium text-base md:text-base text-xs transition-all duration-300 rounded-xl border-0 ${selectedAduSubcategory === subcategory.id ? raisedButtonActiveStyles : raisedButtonStyles}`}>
                {subcategory.name}
              </button>)}
          </div>}

        {/* Photo Grid or Selection Prompt */}
        {showPhotoGrid ? <div className="space-y-8" onMouseMove={handleMouseMove}>
            {/* Title for selected subcategory */}
            {selectedCategory === "adu" && selectedAduSubcategory && <div className="text-center mb-8">
                <h2 className="text-5xl font-thin text-orange-500">
                  {aduSubcategories.find(sub => sub.id === selectedAduSubcategory)?.fullDescription}
                </h2>
              </div>}
            
            {/* Videos Section */}
            {getCurrentVideos().length > 0 && <div className="mb-12">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-semibold text-[#e4683b] mb-3">Videos</h3>
                </div>
                <div className={`${selectedCategory === 'house' ? 'flex justify-center max-w-3xl mx-auto' : getCurrentVideos().length === 1 ? 'flex justify-center max-w-2xl mx-auto' : getCurrentVideos().length === 2 ? 'grid grid-cols-2 gap-8 max-w-4xl mx-auto' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto'}`}>
                  {getCurrentVideos().map(video => <div key={video.id} className="relative group">
                      <div className="aspect-video overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                        <video key={video.id} src={video.videoUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                      </div>
                    </div>)}
                </div>
              </div>}
            
            {/* Photos Section */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-semibold text-[#e4683b] mb-2">Photos</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {getCurrentPhotos().map((photo, index) => <div key={photo.id} className="relative group cursor-pointer" onClick={() => handlePhotoClick(photo, index)} onMouseEnter={() => setHoveredPhoto(photo)} onMouseLeave={() => setHoveredPhoto(null)}>
                    <div className="aspect-[4/3] overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                      <img src={photo.imageUrl} alt={photo.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div> : null}

        {/* Photo Modal */}
        {selectedPhoto && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="relative max-w-4xl max-h-full">
              <button onClick={closePhotoModal} className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors">
                ✕
              </button>
              
              <button onClick={prevPhoto} className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors">
                ←
              </button>
              
              <button onClick={nextPhoto} className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors">
                →
              </button>

              <img src={selectedPhoto.imageUrl} alt={selectedPhoto.title} className="max-w-full max-h-full object-contain" />
              
              <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white p-4">
                <h3 className="font-semibold mb-1">{selectedPhoto.title}</h3>
                <p className="text-sm text-gray-300">{selectedPhoto.description}</p>
              </div>
            </div>
          </div>}


        {/* Hover Description */}
        {hoveredPhoto && !isMobile && !selectedPhoto && <div className="fixed z-40 bg-black/95 text-white p-4 max-w-xs pointer-events-none" style={{
        left: mousePosition.x + 20,
        top: mousePosition.y - 10,
        transform: 'translateY(-50%)'
      }}>
            <h3 className="font-semibold mb-1">{hoveredPhoto.title}</h3>
            <p className="text-sm text-gray-300">{hoveredPhoto.description}</p>
          </div>}
      </div>

      <Footer />
      <FloatingADUWizard />
      <NeumorphicThemeToggle />
    </NeumorphicContainer>;
}

const OurWork = () => {
  return (
    <NeumorphicThemeProvider>
      <OurWorkContent />
    </NeumorphicThemeProvider>
  );
};

export default OurWork;